/* eslint-disable no-undef */
import { defineStore } from 'pinia'
import { useFetch } from '../helpers/fetch'
import { intervals, resolutions, resolutionArrays } from '../helpers/chartConstants'
import { createConsumptionDataItem, createTemperatureDataItem, createEffectDataItem, createDebEffectDataItem, currentEnergyForPeriod, generateEffectDataPoints, generateDebEffectDataPoints } from '../helpers/chartDataNew'
import apiUrl from '../helpers/apiUrl'
// import store from './index'

let env = {}
try {
  env = import.meta.env
  if (typeof env !== 'object') env = {}
} catch (_) {
  env = {}
}
const development = env.STORYBOOK === 'true'

const myPagesWrapper = document.querySelector('div.vue-my-pages')
let isCorporate = myPagesWrapper ? (document.querySelector('meta[name="corporate"]')?.content ?? '') === 'true' : window.location.href.includes('--energikonto')

const formatDateText = (dateText, translations) => {
  if (dateText.length === 0) return '-'

  const dateTextArray = dateText.split('-')

  return `${parseInt(dateTextArray[2], 10)} ${translations.dateFormat.monthsLong[parseInt(dateTextArray[1]) - 1]} ${dateTextArray[0]}`
}

const formatDateTextWeekly = (dateTextFrom, dateTextTo, translations) => {
  if (dateTextFrom.length === 0 || dateTextTo.length === 0) return '-'

  const dateText1 = dateTextFrom.split('-')
  const dateText2 = dateTextTo.split('-')

  if (new Date(dateTextFrom).getMonth() === new Date(dateTextTo).getMonth()) {
    //same month
    return `${parseInt(dateText1[2], 10)} - ${parseInt(dateText2[2], 10)} ${translations.dateFormat.monthsLong[parseInt(dateText1[1]) - 1]} ${dateText2[0]}`
  } else {
    //different months
    return `${dateArrayInText(dateTextFrom.split('-'), translations)} - ${dateArrayInText(dateTextTo.split('-'), translations)}`
  }
}

const dateArrayInText = (textArray, translations) => {
  return `${parseInt(textArray[2], 10)} ${translations.dateFormat.monthsShortAlt[parseInt(textArray[1]) - 1]} ${textArray[0]}`
}

let initialStore = {
  currentDeliverySite: null,
  currentCustomer: null,
  pending: true,
  initialLoad: true,
  initialEnergyDataFetch: true,
  isMobile: window.matchMedia(' (max-width: 650px) ').matches,
  interval: {
    options: [
      {
        value: intervals.YEARLY,
        displayName: '{År}',
        disabled: false,
        selected: true,
        drilldownOption: intervals.MONTHLY,
        hidden: false
      },
      {
        value: intervals.MONTHLY,
        displayName: '{Månad}',
        disabled: false,
        selected: false,
        drilldownOption: intervals.DAILY,
        hidden: false
      },
      {
        value: intervals.WEEKLY,
        displayName: '{Vecka}',
        disabled: false,
        selected: false,
        drilldownOption: intervals.DAILY,
        hidden: false
      },
      {
        value: intervals.DAILY,
        displayName: '{Dag}',
        disabled: false,
        selected: false,
        nextIntervalOption: null,
        hidden: false
      }
    ]
  },
  resolution: {
    options: resolutionArrays.YEARLY,
    resolutionArrays: resolutionArrays
  },
  settings: {
    open: false,
    comparisonPreviousPeriod: false,
    comparisonPreviousYear: false,
    temperature: false,
    effectPeaks: false,
    resolutionInputChanged: false,
    quartersExist: true,
    quarterlyPeriodExist: false,
    energyDataToggle: false,
    costBasedToggle: false,
    hasTemporaryConsumptionData: false,
    hasTemporaryCompareConsumptionData: false
  },
  pagination: {
    isPrevious: false,
    isNext: false,
    previousDisabled: false,
    nextDisabled: true,
    text: new Date().getFullYear()
  },
  summary: {
    currentPeriod: {
      text: null,
      val: '', //'- kWh',
      from: `${new Date().getFullYear()}-01-01`,
      to: `${new Date().getFullYear()}-12-31`
    },
    hoverPeriod: {
      text: null,
      longText: null,
      val: '' //'- kWh'
    },
    hoverPointIsTemporary: null,
    temperature: {
      text: null,
      value: null
    },
    compare: {
      text: null,
      value: null,
      from: null,
      to: null
    },
    hoverCompare: {
      text: null,
      longText: null,
      val: '' //'- kWh'
    },
    effectPeaks: {
      averagePowerValue: null,
      averagePowerLabel: null,
      items: [],
      activeItemIndex: null,
      priceCalculationData: null
    },
    debEffectPeaks: {
      averagePowerValue: null,
      averagePowerLabel: null,
      items: [],
      activeItemIndex: null,
      priceCalculationData: null
    },
    quarters: null,
    quartersReactive: null
  },
  energyData: null,
  compareEnergyData: null,
  temperatureData: null,
  effectData: null,
  missingEffectData: null,
  debEffectData: null,
  missingDebEffectData: null,
  disableDebEffectPeaks: false,
  errorEmptyState: null,
  translations: null,
  featureSettings: {
    isEffectPilot: false,
    showZoom: false,
    powerTariffForecast: false,
    isPowerTariff2025: false
  },
  usePowerTariffPeriod: true,
  isCorporate: isCorporate,
  lowestResolution: null,
  tabValue: null, // Currently string, like 'Produktion'. Should maybe be an ID or index instead of translated label.
  exportLinks: [],
  maxEffectTariffValue: 0
}

const getInitialIntervalOptions = (storeReset) => {
  const deliverySiteAllOrGroups = storeReset.currentDeliverySiteId === 'all' || storeReset.currentDeliverySiteId?.includes('group/')
  const isConsumptionSite = storeReset.currentDeliverySite?.isConsumptionSite

  const hasHourlyMeasuringMethod = storeReset.currentDeliverySite?.hasHourlyMeasuringMethod
  const isEffectPilot = storeReset.featureSettings.isEffectPilot ? storeReset.currentDeliverySite?.consumption?.isPowerTariff : false
  const isPowerTariff = storeReset.featureSettings.powerTariffForecast || storeReset.currentTabIsEffect || storeReset.currentDeliverySite?.consumption?.isPowerTariff
  if (!deliverySiteAllOrGroups && isConsumptionSite && ((hasHourlyMeasuringMethod && isEffectPilot) || isPowerTariff)) {
    return storeReset.interval.options.map((item) => ({ ...item, selected: item.value === intervals.MONTHLY }))
  } else {
    return storeReset.interval.options.map((item) => ({ ...item, selected: item.value === intervals.YEARLY }))
  }
}

export const useChartStore = defineStore('chart', {
  persist: {
    key: isCorporate ? 'pinia-corp' : 'pinia',
    storage: sessionStorage,
    paths: isCorporate ? ['currentCustomer', 'translations'] : ['currentDeliverySite', 'currentCustomer', 'translations']
  },
  state: () => JSON.parse(JSON.stringify(initialStore)),
  getters: {
    currentPeriod: (state) => (state.usePowerTariffPeriod && state.featureSettings.powerTariffForecast ? state.currentDeliverySite?.powerTariffPeriod : state.summary.currentPeriod),
    currentDeliverySiteId: (state) => state.currentDeliverySite?.id,
    currentTabValue: (state) => state.tabValue,
    currentDeliverySiteDirection: (state) => state.currentDeliverySite?.consumption?.direction,
    currentDeliverySiteIsAllOrGroup: (state) => state.currentDeliverySiteId === 'all' || state.currentDeliverySiteId?.includes('group/'),
    effectPeaksChoiceShown: (state) =>
      state.featureSettings.isEffectPilot && !(state.currentDeliverySiteId === 'all' || state.currentDeliverySiteId?.includes('group/')) && !state.isCorporate && state.currentDeliverySite?.isConsumptionSite && state.currentDeliverySiteHasHourlyMeasuringMethod,
    currentDeliverySiteIsConsumptionSite: (state) => state.currentDeliverySite?.isConsumptionSite,
    currentDeliverySiteIsPowerTariff: (state) => state.currentDeliverySite?.consumption?.isPowerTariff,
    currentDeliverySiteHasHourlyMeasuringMethod: (state) => state.currentDeliverySite?.hasHourlyMeasuringMethod,
    currentCustomerId: (state) => state.currentCustomer?.customerNumber,
    currentResolution: (state) => (tempResolution) => tempResolution ?? state.resolution.options.find((item) => item.selected),
    currentInterval: (state) => state.interval.options.find((item) => item.selected),
    currentTemperatureVisibility: (state) => state.settings.temperature,
    settingsChangedBadgeActive: (state) => state.settings.temperature || state.settings.effectPeaks || state.settings.comparisonPreviousPeriod || state.settings.comparisonPreviousYear || state.settings.resolutionInputChanged,
    currentTabIsEffect: (state) => state.tabValue === 'Effekt',
    currentTabIsEnergy: (state) => state.tabValue === 'Energi'
  },
  actions: {
    $reset(storeValues = {}) {
      const storeReset = { ...initialStore, ...storeValues }
      const initialIntervalOptions = getInitialIntervalOptions(storeReset)

      if (initialIntervalOptions) {
        storeReset.interval.options = initialIntervalOptions
      }
      this.$patch(storeReset)
      this.updateResolutionAndSetFromTo()
    },
    setPropsFromPage(translations, showZoom, isEffectPilot, isPowerTariffForecast, isPowerTariff2025) {
      this.translations = translations
      this.featureSettings.powerTariffForecast = isPowerTariffForecast
      if (!initialStore.translations) initialStore.translations = translations
      if (!this.featureSettings.showZoom) this.featureSettings.showZoom = showZoom

      if (isEffectPilot) {
        this.featureSettings.isEffectPilot = true
        if (this.currentDeliverySiteIsConsumptionSite !== undefined && this.currentDeliverySiteIsPowerTariff !== undefined) {
          this.settings.effectPeaks = this.effectPeaksChoiceShown && this.currentDeliverySiteIsConsumptionSite && this.currentDeliverySiteIsPowerTariff
        } else {
          this.settings.effectPeaks = false
        }
      } else if (isPowerTariffForecast || isPowerTariff2025) {
        this.featureSettings.isPowerTariff2025 = isPowerTariff2025
      }

      initialStore = { ...initialStore, featureSettings: { showZoom, isEffectPilot, powerTariffForecast: isPowerTariffForecast, isPowerTariff2025 } }
      if (this.currentDeliverySiteId) {
        const initialIntervalOptions = getInitialIntervalOptions(this)

        if (initialIntervalOptions) {
          this.interval.options = initialIntervalOptions
          this.updateResolutionAndSetFromTo()
        }
      }

      this.setTranslations(translations)
    },
    setTranslations(translations) {
      // Translate intervals
      const translatedIntervalOptions = this.interval.options.map((item) => ({
        ...item,
        displayName: translations.interval.options[item.value.toLowerCase()]
      }))

      initialStore.interval.options = translatedIntervalOptions
      this.interval.options = translatedIntervalOptions

      // Translate resolutions
      const translatedResolutionOptions = {}
      const resolutionKeys = Object.keys(initialStore.resolution.resolutionArrays)

      resolutionKeys.forEach((key) => {
        translatedResolutionOptions[key] = resolutionArrays[key].map((item) => ({
          ...item,
          displayName: translations.resolution.options[item.value.toLowerCase()]
        }))
      })

      initialStore.resolution.resolutionArrays = translatedResolutionOptions
      this.resolution.resolutionArrays = translatedResolutionOptions
      initialStore.resolution.options = translatedResolutionOptions[this.currentInterval.value.toUpperCase()]
      this.resolution.options = translatedResolutionOptions[this.currentInterval.value.toUpperCase()]
    },
    /**
     * Sends a request for energy data based on the store values.
     * Updates the store values based on the response.
     *x
     * @param {object} params An object containing options to overrides pre saved store values. (comparisonPreviousPeriod, comparisonPreviousYear, resolution and effectPeaks)
     *
     * @returns Returns true or false based in the response status.
     */
    async fetchEnergyData(params) {
      if (!this.currentDeliverySiteId) return null
      const currentTabIsEffect = this.tabValue === 'Effekt'
      const apiParams = new URLSearchParams()

      if (development) apiParams.set('PINIA', 'x')
      apiParams.set('from', this.currentPeriod.from.toBackendDate())
      apiParams.set('to', this.currentPeriod.to.toBackendDate())
      apiParams.set('previous', this.pagination.isPrevious)
      apiParams.set('next', this.pagination.isNext)
      apiParams.set('resolution', this.currentResolution(params?.resolution)?.value)
      apiParams.set('interval', this.currentInterval.value)
      apiParams.set('isPowerTariff', this.currentDeliverySiteIsPowerTariff && currentTabIsEffect)
      apiParams.set('isRolling12Months', this.featureSettings.powerTariffForecast)

      if (this.featureSettings.powerTariffForecast) {
        this.settings.costBasedToggle = true
        // Only use start date once
        this.usePowerTariffPeriod = false
      }

      if (this.currentCustomerId) apiParams.set('customerId', this.currentCustomerId)

      // Set consumption or production
      if (this.currentDeliverySiteIsConsumptionSite !== undefined) {
        let direction = this.currentDeliverySiteIsConsumptionSite ? 'Consumption' : 'Production'

        const condition = this.isCorporate && (this.currentDeliverySite?.id === 'all' || this.currentDeliverySite?.id.includes('group/'))
        if (condition && this.tabValue) {
          if (this.tabValue === 'Produktion') direction = 'Production'
          if (this.tabValue === 'Elanvändning') direction = 'Consumption'
        }

        apiParams.set('direction', direction)
      }

      if (this.tabValue === 'Reaktiv') apiParams.set('isReactive', true)
      if (this.tabValue !== 'Effekt') {
        if (params?.comparisonPreviousPeriod !== null && params?.comparisonPreviousPeriod !== undefined) {
          apiParams.set('comparePrevious', params.comparisonPreviousPeriod)
        } else {
          apiParams.set('comparePrevious', this.settings.comparisonPreviousPeriod)
        }

        if (params?.comparisonPreviousYear !== null && params?.comparisonPreviousYear !== undefined) {
          apiParams.set('comparePreviousYear', params.comparisonPreviousYear)
        } else {
          apiParams.set('comparePreviousYear', this.settings.comparisonPreviousYear)
        }
      }

      const responseObject = await useFetch(`${apiUrl.getConsumptionChartData}${this.currentDeliverySiteId}?${apiParams.toString()}`)
      const response = responseObject?.response
      //INFO: Return false when response is missing or response status is not equal to 200 or meta response status is equal to 204 (empty)
      if (!response || responseObject?.status !== 200 || response.meta.apiResponse?.status === 204) {
        const statusCode = response.meta?.apiResponse?.status ?? responseObject?.status
        const errorMessages = response.meta?.apiResponse?.message ?? this.translations.errorMessages
        this.errorEmptyState = this.formatErrorEmptyResponse(statusCode, errorMessages)
        return false
      }

      this.disableDebEffectPeaks = response.data?.disableDebMaxPower || false

      // Initially show deb effect peaks.
      if (this.initialEnergyDataFetch && !this.disableDebEffectPeaks && currentTabIsEffect) {
        this.settings.costBasedToggle = true
      }

      if (this.disableDebEffectPeaks) {
        this.settings.costBasedToggle = false
      }

      if (response.data.consumptions?.length > 0) {
        // Energy data
        if (currentTabIsEffect) {
          this.energyData = null
        } else {
          this.energyData = createConsumptionDataItem(response.data.consumptions, this.currentResolution(params?.resolution)?.value, false, this?.tabValue, null, this.featureSettings?.powerTariff)
        }

        this.settings.quartersExist = response.data.consumptions.some((item) => item.quarters !== undefined && item.quarters !== null)
        this.settings.quarterlyPeriodExist = response.data.summary.availablePeriods.some((item) => item.resolution === resolutions.QUARTERLY)
        this.settings.hasTemporaryConsumptionData = this.energyData?.some((item) => item?.statusMeasured === 'Temporary')

        // Deb effect peaks
        if ((this.featureSettings.powerTariffForecast || currentTabIsEffect) && !this.disableDebEffectPeaks) {
          /* lade in dessa snabbt baserat på hur det funkade innan - detaljer kan behöva ses över */
          this.summary.debEffectPeaks = generateDebEffectDataPoints(response.data.consumptions, this.currentResolution(), response.data.summary.monthlyPowerInformation)
          this.debEffectData = createDebEffectDataItem(response.data.consumptions, this.currentResolution(params?.resolution)?.value, this.featureSettings?.powerTariffForecast)
          // The missing data points always need a 0 y-value (so the staples needed to hover over a point are always invisible, but hoverable)
          this.missingDebEffectData = this.debEffectData.filter((item) => item.tooltipText?.length > 0).map((item) => ({ ...item, y: 0 }))
        }
        if (currentTabIsEffect || ((this.featureSettings.isEffectPilot || this.featureSettings.powerTariffForecast) && this.currentInterval.value === intervals.MONTHLY)) {
          this.summary.effectPeaks = generateEffectDataPoints(response.data.consumptions, this.currentResolution(), response.data.summary.monthlyPowerInformation)
          this.effectData = createEffectDataItem(response.data.consumptions, this.currentResolution(params?.resolution)?.value, this.featureSettings?.powerTariffForecast)
          // The missing data points always need a 0 y-value (so the staples needed to hover over a point are always invisible, but hoverable)
          this.missingEffectData = this.effectData.filter((item) => item.tooltipText?.length > 0).map((item) => ({ ...item, y: 0 }))
        } else {
          this.summary.effectPeaks = {
            averagePowerValue: null,
            averagePowerLabel: null,
            items: null,
            activeItemIndex: null
          }
          this.effectData = null
        }
      }
      if (response.data.compareData?.consumptions?.length > 0) {
        this.compareEnergyData = createConsumptionDataItem(response.data.compareData.consumptions, this.currentResolution(params?.resolution)?.value, true, this?.tabValue)
        this.settings.hasTemporaryCompareConsumptionData = this.energyData.some((item) => item?.statusMeasured === 'Temporary')
      }

      this.lowestResolution = response.data.lowestResolution

      //keep logic the same as in the old chart (setting disabled and selected at the same time)
      const isPowerTariffPilot = this.featureSettings.isEffectPilot ? this.currentDeliverySiteIsPowerTariff : false
      const conditionPowerTariffDefault = !this.currentDeliverySiteIsAllOrGroup && !this.isCorporate && this.currentDeliverySiteIsConsumptionSite && this.currentDeliverySiteHasHourlyMeasuringMethod && isPowerTariffPilot
      const conditionSetIntervalToYearly = !conditionPowerTariffDefault
      const conditionDisableChoices = conditionPowerTariffDefault && this.settings.effectPeaks ? false : this.lowestResolution === intervals.MONTHLY
      const isPowerTariff = this.featureSettings.powerTariffForecast || this.tabValue === 'Effekt'

      if (this.initialLoad) {
        if (isPowerTariff) {
          this.interval.options = this.interval.options.map((option) => ({ ...option, selected: option.value === intervals.MONTHLY }))
        } else if (conditionSetIntervalToYearly && conditionDisableChoices) {
          this.interval.options = this.interval.options.map((option) => ({ ...option, disabled: option.value !== intervals.YEARLY, selected: option.value === intervals.YEARLY }))
          this.resolution.options = resolutionArrays.YEARLY.map((option) => ({ ...option, disabled: option.value !== resolutions.MONTHLY, selected: option.value === resolutions.MONTHLY }))
        } else if (conditionSetIntervalToYearly) {
          this.interval.options = this.interval.options.map((option) => ({ ...option, selected: option.value === intervals.YEARLY }))
          this.resolution.options = resolutionArrays.YEARLY.map((option) => ({ ...option, selected: option.value === resolutions.MONTHLY }))
        } else if (conditionDisableChoices) {
          this.interval.options = this.interval.options.map((option) => ({ ...option, disabled: option.value !== intervals.YEARLY }))
          this.resolution.options = resolutionArrays.YEARLY.map((option) => ({ ...option, disabled: option.value !== resolutions.MONTHLY }))
        }
      }

      this.interval.options = this.interval.options.map((option) => ({ ...option, hidden: (currentTabIsEffect && option.value === intervals.YEARLY) || option.value === intervals.WEEKLY }))
      this.summary.currentPeriod.from = response.data.responseStartPeriod.toBackendDate()
      this.summary.currentPeriod.to = response.data.responseEndPeriod.toBackendDate()
      this.summary.currentPeriod.val = currentEnergyForPeriod(response.data, this.currentDeliverySiteId, this.currentTabValue) //+ typeOfEnergy === 'reactive' ? 'kVArh' : 'kWh' //response.data.energyForPeriod

      if (this.summary.currentPeriod.from.trim() === this.summary.currentPeriod.to.trim()) {
        this.summary.currentPeriod.text = `${formatDateText(this.summary.currentPeriod.to, this.translations)}`
      } else {
        //text for current period also depends on currently selected interval
        if (this.currentInterval.value === intervals.MONTHLY) {
          let tmpArray = formatDateText(this.summary.currentPeriod.from, this.translations).split(' ')
          tmpArray.shift()
          this.summary.currentPeriod.text = tmpArray.join(' ')
        } else if (this.currentInterval.value === intervals.YEARLY) {
          //if resolution is changing to weekly and starting month is december then we need to add 1 to the year
          if ((this.currentResolution(params?.resolution)?.value === resolutions.WEEKLY || this.currentResolution(params?.resolution)?.value === resolutions.MONTHLY) && this.summary.currentPeriod.from.split('-')[1] === '12') {
            this.summary.currentPeriod.text = (Number(this.summary.currentPeriod.from.split('-')[0]) + 1).toString()
          } else {
            this.summary.currentPeriod.text = this.summary.currentPeriod.from.split('-')[0]
          }
        } else if (this.currentInterval.value === intervals.WEEKLY) {
          this.summary.currentPeriod.text = formatDateTextWeekly(this.summary.currentPeriod.from, this.summary.currentPeriod.to, this.translations)
          //this.summary.currentPeriod.text = `${formatDateText(this.summary.currentPeriod.from, this.translations)} - ${formatDateText(this.summary.currentPeriod.to, this.translations)}`
        } else {
          this.summary.currentPeriod.text = `${formatDateText(this.summary.currentPeriod.from, this.translations)} - ${formatDateText(this.summary.currentPeriod.to, this.translations)}`
        }
      }

      if (this.compareEnergyData && response.data.compareData) {
        this.summary.compare.from = response.data.compareData.responseStartPeriod.toBackendDate()
        this.summary.compare.to = response.data.compareData.responseEndPeriod.toBackendDate()

        if (this.summary.compare.from.trim() === this.summary.compare.to.trim()) {
          this.summary.compare.text = `${formatDateText(this.summary.compare.to, this.translations)}`
        } else {
          if (this.currentInterval.value === intervals.MONTHLY) {
            let tmpArray = formatDateText(this.summary.compare.from, this.translations).split(' ')
            tmpArray.shift()
            this.summary.compare.text = tmpArray.join(' ')
          } else if (this.currentInterval.value === intervals.YEARLY) {
            this.summary.compare.text = this.summary.compare.from.split('-')[0]
          } else if (this.currentInterval.value === intervals.WEEKLY) {
            this.summary.compare.text = formatDateTextWeekly(this.summary.compare.from, this.summary.compare.to, this.translations)
          } else {
            this.summary.compare.text = `${formatDateText(this.summary.compare.from, this.translations)} - ${formatDateText(this.summary.compare.to, this.translations)}`
          }
        }

        this.summary.compare.val = currentEnergyForPeriod(response.data.compareData, this.currentDeliverySiteId)
      }
      this.pagination.nextDisabled = !response.data.showNextButton
      this.pagination.previousDisabled = !response.data.showPreviousButton

      this.exportLinks = response.data.exportLinks
      this.initialLoad = false

      if (this.settings.temperature) {
        await this.fetchTemperatureData()
      }

      this.initialEnergyDataFetch = false

      return true
    },
    /**
     * Sends a request for temperature data based on the store values.
     * Updates the store values based on the response.
     *
     * @returns Returns true or false based in the response status.
     */
    async fetchTemperatureData(params) {
      const apiParams = new URLSearchParams()
      if (development) apiParams.set('PINIA', 'x')
      apiParams.set('from', this.summary.currentPeriod.from)
      apiParams.set('to', this.summary.currentPeriod.to)
      apiParams.set('resolution', this.currentResolution(params?.resolution).value)
      apiParams.set('interval', this.currentInterval.value)
      if (this.isCorporate) apiParams.set('customerId', this.currentCustomerId)
      const responseObject = await useFetch(`${apiUrl.getTemperatureChartData}${this.currentDeliverySiteId}?${apiParams.toString()}`)
      const response = responseObject?.response

      if (!response || response.meta.apiResponse?.status === 204) return false

      if (response.data?.length > 0) this.temperatureData = createTemperatureDataItem(response.data, this.currentResolution(params?.resolution)?.value)
      else this.temperatureData = null

      return true
    },
    async fetchDrilldownEnergyData(e) {
      if (this.isMobile) return false

      const resolutionBeforeDrillDown = this.currentResolution().value

      this.pending = true
      this.summary.currentPeriod.from = e.point.start.substring(0, 10)
      this.summary.currentPeriod.to = e.point.end.substring(0, 10)
      this.resolution.options = this.resolution.resolutionArrays[this.currentInterval.drilldownOption.toUpperCase()]
      this.interval.options = this.interval.options.map((option) => ({ ...option, selected: option.value === resolutionBeforeDrillDown }))

      await this.fetchEnergyData()

      this.pending = false
    },
    async fetchEffectPriceCalculations() {
      if (!this.currentDeliverySiteId) return null

      this.pending = true

      const apiParams = new URLSearchParams()
      if (development) apiParams.set('PINIA', 'x')
      apiParams.set('from', this.summary.currentPeriod.from)
      apiParams.set('to', this.summary.currentPeriod.to)
      apiParams.set('resolution', this.currentResolution().value)

      const responseObject = await useFetch(`${apiUrl.getEffectPriceCalculationData}${this.currentDeliverySiteId}?${apiParams.toString()}`)
      const response = responseObject?.response

      if (!response || response.meta.apiResponse?.status === 204) {
        this.pending = false
        return false
      }

      if (response.data) this.summary.effectPeaks.priceCalculationData = response.data

      this.pending = false

      return true
    },
    async openSettingsPanel() {
      this.settings.open = true
    },
    async closeSettingsPanel() {
      this.settings.open = false
    },
    async saveSettings(params) {
      this.closeSettingsPanel()

      let requestsSuccessfull = true
      this.pending = true

      if (params.temperature === true) {
        const response = await this.fetchTemperatureData(params)
        if (!response) requestsSuccessfull = false
      }

      if (
        (this.settings.comparisonPreviousPeriod !== params.comparisonPreviousPeriod && params.comparisonPreviousPeriod === true) ||
        (this.settings.comparisonPreviousYear !== params.comparisonPreviousYear && params.comparisonPreviousYear === true) ||
        (this.settings.effectPeaks !== params.effectPeaks && params.effectPeaks === true) ||
        (params.resolution && this.currentResolution()?.value !== params.resolution?.value)
      ) {
        const response = await this.fetchEnergyData(params)
        if (response === false) requestsSuccessfull = false
      }

      if (requestsSuccessfull) {
        this.settings.comparisonPreviousPeriod = params.comparisonPreviousPeriod ?? false
        this.settings.comparisonPreviousYear = params.comparisonPreviousYear ?? false
        this.settings.effectPeaks = params.effectPeaks ?? this.settings.effectPeaks
        this.settings.temperature = params.temperature ?? false
        this.settings.costBasedToggle = params.costBasedToggle ?? this.settings.costBasedToggle

        if (params.effectPeaks === false)
          this.summary.effectPeaks = {
            priceCalculationData: null,
            averagePowerValue: null,
            averagePowerLabel: null,
            items: null,
            activeItemIndex: null
          }

        if (params.resolution && this.currentResolution()?.value !== params.resolution?.value) {
          this.resolution.options = this.resolution.options.map((option) => ({ ...option, selected: option.value === params.resolution.value }))
        }
      } else {
        //TODO: Show toast => how do we access Vuex store?
        alert('saveSettings - error')
      }

      this.pending = false
    },
    /**
     * Save the new interval to the store and automatically fetch the new energy data.
     *
     * @param {string} newInterval The interval value as string set in the constants, eg: Yearly
     */
    async saveInterval(params) {
      if (this.currentInterval.value === params.interval.value) return

      let requestsSuccessfull = true
      this.pending = true

      this.interval.options = this.interval.options.map((option) => ({ ...option, selected: option.value === params.interval.value }))

      //TODO: refactor/remove if not needed, also look into availablePeriods.. check old logic
      this.updateResolutionAndSetFromTo()

      const response = await this.fetchEnergyData()

      if (response === false) requestsSuccessfull = false

      if (!requestsSuccessfull) {
        //TODO: Show toast => how do we access Vuex store?
        alert('saveSettings - error')
      }

      if (this.settings.temperature) {
        await this.fetchTemperatureData()
      }

      this.pending = false
    },
    updateResolutionAndSetFromTo() {
      const now = new Date()
      // Yearly
      if (this.currentInterval.value === intervals.YEARLY) {
        this.resolution.options = resolutionArrays.YEARLY
        this.summary.currentPeriod.from = now.getFirstDayOfYear().toBackendDate()
        this.summary.currentPeriod.to = now.getLastDayOfYear().toBackendDate()
      }
      // Monthly
      else if (this.currentInterval.value === intervals.MONTHLY) {
        this.resolution.options = resolutionArrays.MONTHLY
        // Power tariff gets the period from the consumption api call
        if (!this.featureSettings.powerTariffForecast) {
          this.summary.currentPeriod.from = now.getFirstDayOfMonth().toBackendDate()
          this.summary.currentPeriod.to = now.getLastDayOfMonth().toBackendDate()
        }
      }
      // Weekly
      else if (this.currentInterval.value === intervals.WEEKLY) {
        this.resolution.options = resolutionArrays.WEEKLY
        this.summary.currentPeriod.from = now.getFirstDayOfWeek().toBackendDate()
        this.summary.currentPeriod.to = now.getLastDayOfWeek().toBackendDate()
      }
      // Daily
      else if (this.currentInterval.value === intervals.DAILY) {
        this.resolution.options = resolutionArrays.DAILY
        this.summary.currentPeriod.from = new Date().toISOString().slice(0, 10)
        this.summary.currentPeriod.to = new Date().toISOString().slice(0, 10)
      }
    },
    formatErrorEmptyResponse(statusCode, errorMessages) {
      if (statusCode === 200) return null
      if (!errorMessages || (!errorMessages.heading && !errorMessages[statusCode]))
        return {
          status: statusCode,
          heading: `${statusCode}`,
          message: 'Translation missing'
        }

      if (errorMessages.heading) {
        return {
          status: statusCode,
          heading: errorMessages.heading,
          message: errorMessages.text
        }
      }

      return {
        status: statusCode,
        heading: errorMessages[statusCode].heading,
        message: errorMessages[statusCode].message
      }
    },
    async clickNext() {
      this.pending = true
      this.pagination.isNext = true

      await this.fetchEnergyData()
      if (this.settings.temperature) await this.fetchTemperatureData()

      this.pagination.isNext = false
      this.pending = false
    },
    async clickPrevious() {
      this.pending = true
      this.pagination.isPrevious = true

      await this.fetchEnergyData()

      if (this.settings.temperature) {
        await this.fetchTemperatureData()
      }

      this.pagination.isPrevious = false
      this.pending = false
    },
    setHighestEffectPeakValue(value) {
      this.maxEffectTariffValue = value
    }
  }
})
